"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle, Clock, AlertTriangle, XCircle, Info } from "lucide-react";
import { formatDate } from "@/lib/formatting";
import type { DealVerification, VerificationStatus as VerificationStatusType } from "@/lib/deals/verification";
const STATUS_CONFIG: Record<VerificationStatusType, {
  icon: React.ComponentType<{
    className?: string;
  }>;
  bg: string;
  text: string;
  label: string;
  description: string;
}> = {
  pending: {
    icon: Clock,
    bg: "bg-amber-50",
    text: "text-amber-700",
    label: "Wird geprüft",
    description: "Ihr Ökokonto-Dokument wird von unserem Team geprüft."
  },
  verified: {
    icon: CheckCircle,
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    label: "Verifiziert",
    description: "Ihr Ökokonto wurde erfolgreich verifiziert."
  },
  failed: {
    icon: XCircle,
    bg: "bg-red-50",
    text: "text-red-700",
    label: "Abgelehnt",
    description: "Die Verifizierung konnte nicht abgeschlossen werden."
  },
  needs_info: {
    icon: Info,
    bg: "bg-blue-50",
    text: "text-blue-700",
    label: "Weitere Informationen benötigt",
    description: "Bitte laden Sie zusätzliche Dokumente hoch."
  }
};
interface VerificationStatusProps {
  verification: DealVerification | null;
  showDetails?: boolean;
  className?: string;
}

/**
 * VerificationStatus - Displays the current verification status
 */
export function VerificationStatus(t0) {
  const $ = _c(32);
  const {
    verification,
    showDetails: t1,
    className: t2
  } = t0;
  const showDetails = t1 === undefined ? true : t1;
  const className = t2 === undefined ? "" : t2;
  if (!verification || !verification.okokonto_document_url) {
    const t3 = `rounded-lg border border-zinc-200 bg-zinc-50 p-4 ${className}`;
    let t4;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-zinc-500" /></div><div><p className="font-medium text-zinc-700">Dokument ausstehend</p><p className="text-sm text-zinc-500">Bitte laden Sie Ihr Ökokonto-Dokument hoch.</p></div></div>;
      $[0] = t4;
    } else {
      t4 = $[0];
    }
    let t5;
    if ($[1] !== t3) {
      t5 = <div className={t3}>{t4}</div>;
      $[1] = t3;
      $[2] = t5;
    } else {
      t5 = $[2];
    }
    return t5;
  }
  const config = STATUS_CONFIG[verification.verification_status];
  const StatusIcon = config.icon;
  const t3 = `rounded-lg border ${config.bg} p-4 ${className}`;
  const t4 = `w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`;
  const t5 = `w-5 h-5 ${config.text}`;
  let t6;
  if ($[3] !== StatusIcon || $[4] !== t5) {
    t6 = <StatusIcon className={t5} />;
    $[3] = StatusIcon;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t4 || $[7] !== t6) {
    t7 = <div className={t4}>{t6}</div>;
    $[6] = t4;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = `font-medium ${config.text}`;
  let t9;
  if ($[9] !== config.label || $[10] !== t8) {
    t9 = <p className={t8}>{config.label}</p>;
    $[9] = config.label;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== config.description) {
    t10 = <p className="text-sm text-zinc-600 mt-0.5">{config.description}</p>;
    $[12] = config.description;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== showDetails || $[15] !== verification.admin_feedback) {
    t11 = showDetails && verification.admin_feedback && <div className="mt-3 p-3 bg-white/50 rounded-lg border border-zinc-200"><p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-1">Feedback vom Team</p><p className="text-sm text-zinc-700">{verification.admin_feedback}</p></div>;
    $[14] = showDetails;
    $[15] = verification.admin_feedback;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== showDetails || $[18] !== verification.okokonto_document_name || $[19] !== verification.uploaded_at) {
    t12 = showDetails && verification.okokonto_document_name && <div className="mt-3 flex items-center gap-2 text-sm text-zinc-600"><span className="font-medium">Dokument:</span><span>{verification.okokonto_document_name}</span>{verification.uploaded_at && <span className="text-zinc-400">({formatDate(verification.uploaded_at)})</span>}</div>;
    $[17] = showDetails;
    $[18] = verification.okokonto_document_name;
    $[19] = verification.uploaded_at;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t12 || $[24] !== t9) {
    t13 = <div className="flex-1 min-w-0">{t9}{t10}{t11}{t12}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t9;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t7) {
    t14 = <div className="flex items-start gap-3">{t7}{t13}</div>;
    $[26] = t13;
    $[27] = t7;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t14 || $[30] !== t3) {
    t15 = <div className={t3}>{t14}</div>;
    $[29] = t14;
    $[30] = t3;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}